import { useState, useCallback, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Polygon, Marker, useMapEvents, useMap } from "react-leaflet";
import { MapPin, Plus, Trash2, Save, ArrowLeft, X, Pencil, List, PlusCircle, Loader2, Navigation, Search, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CulturePicker from "@/components/CulturePicker";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { createManualParcel, getManualParcels, deleteParcel, updateParcel, followParcel } from "@/lib/api";
import { toast } from "sonner";
import { DRAW_LAYER, MAX_ZOOM } from "@/lib/mapLayers";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const markerIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

function FlyToPosition({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo([position.lat, position.lng], position.zoom || 16, { duration: 0.8 });
    }
  }, [position, map]);
  return null;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
}


export default function ManualParcelEditor({ onBack, onParcelCreated, initialCenter, initialZoom, prefill }) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("create");
  const [points, setPoints] = useState([]);
  const [name, setName] = useState("");
  const [commune, setCommune] = useState("");
  const [cultureType, setCultureType] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [manualLat, setManualLat] = useState("");
  const [manualLon, setManualLon] = useState("");
  const [manualParcels, setManualParcels] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editingParcel, setEditingParcel] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [locating, setLocating] = useState(false);
  const [addressQuery, setAddressQuery] = useState("");
  const [addressResults, setAddressResults] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [panelOpen, setPanelOpen] = useState(!isMobile);
  const mapCenter = prefill?.center ? [prefill.center.lat, prefill.center.lon] : (initialCenter || [-21.12, 55.53]);
  // Création depuis le cadastre : contour, commune et nom pré-remplis
  useEffect(() => {
    if (!prefill?.points?.length) return;
    setPoints(prefill.points);
    if (prefill.commune) setCommune(prefill.commune);
    if (prefill.name) setName(prefill.name);
    if (prefill.center) setFlyTarget({ lat: prefill.center.lat, lng: prefill.center.lon, zoom: 17 });
    setPanelOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);
  const mapZoom = initialZoom || 11;

  const fetchManualParcels = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await getManualParcels();
      setManualParcels(res.data);
    } catch { }
    finally { setLoadingList(false); }
  }, []);

  useEffect(() => {
    if (tab === "list") fetchManualParcels();
  }, [tab, fetchManualParcels]);

  const lastTapRef = useRef({ t: 0, lat: 0, lng: 0 });
  const handleMapClick = useCallback((latlng) => {
    // Double-tap sur téléphone = deux touchers : le second, quasi au même endroit, est ignoré
    const now = Date.now(), last = lastTapRef.current;
    if (now - last.t < 450 && Math.abs(latlng.lat - last.lat) < 0.00005 && Math.abs(latlng.lng - last.lng) < 0.00005) return;
    lastTapRef.current = { t: now, lat: latlng.lat, lng: latlng.lng };
    setPoints(prev => [...prev, { lat: parseFloat(latlng.lat.toFixed(6)), lon: parseFloat(latlng.lng.toFixed(6)) }]);
    if (isMobile && !panelOpen) setPanelOpen(true);
  }, [isMobile, panelOpen]);

  const addManualPoint = () => {
    const lat = parseFloat(manualLat);
    const lon = parseFloat(manualLon);
    if (isNaN(lat) || isNaN(lon)) { toast.error("Coordonnees GPS invalides"); return; }
    if (lat < -21.5 || lat > -20.8 || lon < 55.2 || lon > 55.9) {
      toast.error("Coordonnees hors de La Reunion"); return;
    }
    setPoints(prev => [...prev, { lat, lon }]);
    setManualLat(""); setManualLon("");
  };

  const removePoint = (idx) => setPoints(prev => prev.filter((_, i) => i !== idx));

  const handleGeolocate = () => {
    if (!navigator.geolocation) { toast.error("Geolocalisation non supportee"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setFlyTarget({ lat: latitude, lng: longitude, zoom: 17 });
        toast.success(`Position GPS: ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
        setLocating(false);
      },
      () => { toast.error("Impossible d'obtenir votre position"); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSearchAddress = async () => {
    if (!addressQuery.trim()) return;
    setSearchingAddress(true);
    setAddressResults([]);
    try {
      const q = encodeURIComponent(addressQuery.trim());
      const resp = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${q}&lat=-21.1&lon=55.5&limit=5`);
      const data = await resp.json();
      const features = data.features || [];
      if (features.length === 0) toast.error("Aucune adresse trouvee");
      setAddressResults(features.map(f => ({
        label: f.properties?.label || "",
        lat: f.geometry?.coordinates?.[1],
        lon: f.geometry?.coordinates?.[0],
        context: f.properties?.context || "",
      })));
    } catch {
      toast.error("Erreur de recherche");
    } finally {
      setSearchingAddress(false);
    }
  };

  const selectAddress = (addr) => {
    setFlyTarget({ lat: addr.lat, lng: addr.lon, zoom: 17 });
    setAddressResults([]);
    setAddressQuery(addr.label);
    toast.success(`${addr.label}`);
  };

  const resetForm = () => {
    setPoints([]); setName(""); setCommune(""); setCultureType(""); setNotes(""); setEditingParcel(null);
  };

  const handleSave = async () => {
    if (points.length < 3) { toast.error("Minimum 3 points GPS requis"); return; }
    if (!cultureType) { toast.error("Selectionnez un type de culture"); return; }
    setSaving(true);
    try {
      if (editingParcel) {
        await updateParcel(editingParcel.parcel_id, {
          name: name || `${cultureType} - ${commune}`,
          commune, culture_type: cultureType, points, notes,
        });
        toast.success("Parcelle modifiee");
      } else {
        const res = await createManualParcel({
          name: name || `Parcelle manuelle - ${cultureType}`,
          commune, culture_type: cultureType, points, notes,
        });
        const newParcelId = res.data?.parcel_id;
        // Auto-follow the newly created parcel
        if (newParcelId) {
          try {
            await followParcel(newParcelId);
            toast.success("Parcelle creee et ajoutee a vos parcelles suivies");
          } catch {
            toast.success("Parcelle creee");
            toast.info("Ajoutez-la manuellement a vos parcelles suivies");
          }
        } else {
          toast.success("Parcelle creee");
        }
      }
      resetForm();
      if (onParcelCreated) onParcelCreated();
      setTab("list");
      fetchManualParcels();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const handleDelete = async (parcelId) => {
    if (!window.confirm("Supprimer définitivement cette parcelle et son carnet ?")) return;
    try {
      await deleteParcel(parcelId);
      toast.success("Parcelle supprimee");
      fetchManualParcels();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de suppression");
    }
  };

  const handleEdit = (parcel) => {
    setEditingParcel(parcel);
    const geom = parcel.geometry;
    let coords = [];
    if (geom?.type === "Polygon" && geom.coordinates?.[0]) {
      coords = geom.coordinates[0].slice(0, -1);
    } else if (geom?.type === "MultiPolygon" && geom.coordinates?.[0]?.[0]) {
      coords = geom.coordinates[0][0].slice(0, -1);
    }
    setPoints(coords.map(c => ({ lat: c[1], lon: c[0] })));
    setName(parcel.name || "");
    setCommune(parcel.commune || "");
    setCultureType(parcel.culture_type || "");
    setNotes(parcel.notes || "");
    setTab("create");
    if (isMobile) setPanelOpen(true);
  };

  const polygonPositions = points.length >= 3 ? points.map(p => [p.lat, p.lon]) : [];

  // ==================== FORM PANEL CONTENT ====================
  // ⚠️ IMPORTANT : c'est un ELEMENT JSX (const), PAS un composant fonctionnel.
  // Défini comme composant, il serait recréé à chaque render du parent,
  // ce qui remonterait les <Input> et ferait perdre le focus à chaque frappe.
  const panelContent = (
    <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
      <TabsList className="px-4 pt-2 bg-transparent justify-start gap-1 shrink-0">
        <TabsTrigger value="create" className="text-xs h-auto py-1.5 px-3 rounded-full gap-1.5" data-testid="tab-create">
          <PlusCircle className="w-3 h-3" />
          {editingParcel ? "Modifier" : "Creer"}
        </TabsTrigger>
        <TabsTrigger value="list" className="text-xs h-auto py-1.5 px-3 rounded-full gap-1.5" data-testid="tab-list">
          <List className="w-3 h-3" />
          Mes parcelles
        </TabsTrigger>
      </TabsList>

      {/* CREATE TAB */}
      <TabsContent value="create" className="flex-1 flex flex-col overflow-hidden mt-0 data-[state=inactive]:hidden">
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {editingParcel && (
              <div className="flex items-center gap-2 p-2 rounded-lg border" style={{ borderColor: '#E9C46A40', background: '#E9C46A08' }}>
                <Pencil className="w-3.5 h-3.5" style={{ color: '#E9C46A' }} />
                <span className="text-[11px] font-medium" style={{ color: '#6B6B3B' }}>
                  Modification: {editingParcel.parcel_id}
                </span>
                <Button variant="ghost" size="icon" className="h-5 w-5 ml-auto" onClick={resetForm}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: '#4A4A4A' }}>Nom (optionnel)</Label>
              <Input data-testid="parcel-name-input" placeholder="Ex: Ma parcelle de canne" value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-sm bg-white border-black/10" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: '#4A4A4A' }}>Commune</Label>
              <Input data-testid="parcel-commune-input" placeholder="Ex: Saint-Pierre" value={commune} onChange={(e) => setCommune(e.target.value)} className="h-9 text-sm bg-white border-black/10" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: '#4A4A4A' }}>Type de culture *</Label>
              <CulturePicker value={cultureType} onChange={setCultureType} region={mapCenter[1] < 50 ? "mayotte" : "reunion"}
                placeholder="Sélectionnez…" className="text-sm" testId="parcel-culture-select" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: '#4A4A4A' }}>Recherche par adresse</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="address-search-input"
                  placeholder="Ex: 10 rue des Letchis, Saint-Pierre"
                  value={addressQuery}
                  onChange={(e) => setAddressQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchAddress()}
                  className="h-9 text-sm bg-white border-black/10 flex-1"
                />
                <Button
                  data-testid="search-address-btn"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0 border-black/10"
                  onClick={handleSearchAddress}
                  disabled={searchingAddress}
                >
                  {searchingAddress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {addressResults.length > 0 && (
                <div className="space-y-1 mt-1 max-h-[120px] overflow-y-auto">
                  {addressResults.map((addr, i) => (
                    <button
                      key={`addr-${addr.lat}-${addr.lon}-${i}`}
                      onClick={() => selectAddress(addr)}
                      className="w-full text-left px-3 py-2 rounded-lg border border-black/5 hover:border-black/15 transition-colors"
                      data-testid={`address-result-${i}`}
                    >
                      <p className="text-xs font-medium" style={{ color: '#1A1A1A' }}>{addr.label}</p>
                      <p className="text-[10px]" style={{ color: '#7A7A7A' }}>{addr.context}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: '#4A4A4A' }}>Points GPS</Label>
              <p className="text-[10px]" style={{ color: '#7A7A7A' }}>Cliquez sur la carte ou saisissez manuellement</p>
              <div className="flex gap-2">
                <Input data-testid="manual-lat-input" placeholder="Lat (ex: -21.32)" value={manualLat} onChange={(e) => setManualLat(e.target.value)} className="h-9 text-sm bg-white border-black/10 flex-1" type="number" step="0.000001" />
                <Input data-testid="manual-lon-input" placeholder="Lon (ex: 55.47)" value={manualLon} onChange={(e) => setManualLon(e.target.value)} className="h-9 text-sm bg-white border-black/10 flex-1" type="number" step="0.000001" />
                <Button data-testid="add-point-btn" variant="outline" size="icon" className="h-9 w-9 shrink-0 border-black/10" onClick={addManualPoint}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {points.length > 0 && (
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: '#4A4A4A' }}>
                  {points.length} point{points.length > 1 ? 's' : ''}
                </Label>
                <div className="space-y-1 max-h-[180px] overflow-y-auto">
                  {points.map((p, i) => (
                    <div key={`pt-${p.lat.toFixed(6)}-${p.lon.toFixed(6)}-${i}`} className="flex items-center justify-between px-3 py-1.5 rounded-lg border border-black/5 bg-white">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: '#2D6A4F15', color: '#2D6A4F' }}>{i + 1}</span>
                        <span className="text-xs font-mono" style={{ color: '#1A1A1A' }}>{p.lat.toFixed(6)}, {p.lon.toFixed(6)}</span>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removePoint(i)}>
                        <X className="w-3 h-3" style={{ color: '#E07A5F' }} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: '#4A4A4A' }}>Notes (optionnel)</Label>
              <Textarea data-testid="parcel-notes-input" placeholder="Observations, contexte..." value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm bg-white border-black/10 min-h-[60px]" />
            </div>
          </div>
        </ScrollArea>

        <div className="p-4 border-t border-black/5 space-y-2">
          <Button data-testid="save-parcel-btn" onClick={handleSave} disabled={saving || points.length < 3} className="w-full h-10 rounded-full text-sm font-semibold" style={{ background: '#2D6A4F', color: 'white' }}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {editingParcel ? "Enregistrer les modifications" : "Enregistrer la parcelle"}
          </Button>
          {points.length > 0 && points.length < 3 && (
            <p className="text-[10px] text-center" style={{ color: '#E07A5F' }}>Encore {3 - points.length} point{3 - points.length > 1 ? 's' : ''} minimum</p>
          )}
        </div>
      </TabsContent>

      {/* LIST TAB */}
      <TabsContent value="list" className="flex-1 overflow-hidden mt-0 data-[state=inactive]:hidden">
        <ScrollArea className="h-full">
          {loadingList ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#2D6A4F' }} />
            </div>
          ) : manualParcels.length === 0 ? (
            <div className="text-center py-16 px-4">
              <MapPin className="w-8 h-8 mx-auto mb-3" style={{ color: '#4A4A4A' }} />
              <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>Aucune parcelle manuelle</p>
              <p className="text-xs mt-1" style={{ color: '#7A7A7A' }}>Creez votre premiere parcelle dans l'onglet "Creer"</p>
            </div>
          ) : (
            <div className="p-4 space-y-2">
              <p className="text-[10px] uppercase tracking-wider font-medium mb-2" style={{ color: '#4A4A4A' }}>
                {manualParcels.length} parcelle{manualParcels.length > 1 ? 's' : ''} manuelle{manualParcels.length > 1 ? 's' : ''}
              </p>
              {manualParcels.map((p) => (
                <div key={p.parcel_id} className="p-3 rounded-xl border border-black/5 hover:border-black/10 transition-colors" data-testid={`manual-parcel-${p.parcel_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate" style={{ color: '#1A1A1A' }}>{p.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: '#4A4A4A' }}>
                        {p.commune}{p.cirque ? ` (${p.cirque})` : ''} &middot; {p.surface_ha} ha
                      </p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Badge className="text-[9px] font-medium border-0 px-1.5" style={{ background: '#8B5E3C15', color: '#8B5E3C' }}>
                          Saisie manuelle
                        </Badge>
                        <Badge variant="outline" className="text-[9px] font-medium px-1.5">{p.culture_type}</Badge>
                      </div>
                      {p.notes && (
                        <p className="text-[10px] mt-1.5 italic" style={{ color: '#7A7A7A' }}>{p.notes}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button variant="outline" size="icon" className="h-7 w-7 border-black/10" onClick={() => handleEdit(p)} data-testid={`edit-parcel-${p.parcel_id}`}>
                        <Pencil className="w-3 h-3" style={{ color: '#2D6A4F' }} />
                      </Button>
                      <Button variant="outline" size="icon" className="h-7 w-7 border-black/10" onClick={() => handleDelete(p.parcel_id)} data-testid={`delete-parcel-${p.parcel_id}`}>
                        <Trash2 className="w-3 h-3" style={{ color: '#E07A5F' }} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </TabsContent>
    </Tabs>
  );

  // ==================== MAP CONTENT (shared) ====================
  // Élément JSX (et non composant) : un composant défini ici serait recréé à chaque rendu, ce qui
  // remontait la carte à chaque point posé et rejouait le recentrage (retour au zoom 17).
  const mapContent = (
    <>
      <TileLayer
        url={DRAW_LAYER.url}
        attribution={DRAW_LAYER.attribution}
        maxZoom={MAX_ZOOM}
        maxNativeZoom={DRAW_LAYER.maxNativeZoom}
      />
      <MapClickHandler onMapClick={handleMapClick} />
      {flyTarget && <FlyToPosition position={flyTarget} />}
      {points.map((p, i) => (
        <Marker key={`marker-${p.lat.toFixed(6)}-${p.lon.toFixed(6)}-${i}`} position={[p.lat, p.lon]} icon={markerIcon} />
      ))}
      {polygonPositions.length >= 3 && (
        <Polygon
          positions={polygonPositions}
          pathOptions={{ color: "#2D6A4F", weight: 2, fillColor: "#2D6A4F", fillOpacity: 0.3, dashArray: "5,5" }}
        />
      )}
      {manualParcels.map((p) => {
        const geom = p.geometry;
        let coords = [];
        if (geom?.type === "Polygon" && geom.coordinates?.[0]) {
          coords = geom.coordinates[0].map(c => [c[1], c[0]]);
        }
        if (coords.length === 0) return null;
        return (
          <Polygon
            key={p.parcel_id}
            positions={coords}
            pathOptions={{ color: "#E07A5F", weight: 2, fillColor: "#E07A5F", fillOpacity: 0.15 }}
          />
        );
      })}
    </>
  );

  // ==================== MOBILE LAYOUT ====================
  if (isMobile) {
    return (
      <div className="h-full w-full flex flex-col relative" data-testid="manual-parcel-editor">
        {/* Full-screen map */}
        <div className="flex-1 relative">
          <MapContainer center={mapCenter} zoom={mapZoom} maxZoom={MAX_ZOOM} doubleClickZoom={false} style={{ height: "100%", width: "100%" }} className="z-0 manual-editor-map">
            {mapContent}
          </MapContainer>

          {/* Top bar with back + GPS + points badge */}
          <div className="absolute top-3 left-3 right-3 z-[400] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-10 w-10 bg-white/95 backdrop-blur-sm shadow-sm border-black/5" onClick={onBack} data-testid="back-to-map-btn">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="bg-white/95 backdrop-blur-sm px-3 py-2 rounded-full shadow-sm border border-black/5">
                <p className="text-[11px] font-medium" style={{ color: '#1A1A1A' }}>
                  <MapPin className="w-3 h-3 inline-block mr-1" style={{ color: '#2D6A4F' }} />
                  {points.length} pt{points.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <Button
              data-testid="manual-geolocate-btn"
              variant="outline"
              size="icon"
              className="h-10 w-10 bg-white/95 backdrop-blur-sm shadow-sm border-black/5"
              onClick={handleGeolocate}
              disabled={locating}
            >
              <Navigation className={`w-4 h-4 ${locating ? 'animate-pulse' : ''}`} style={{ color: '#2D6A4F' }} />
            </Button>
          </div>

          {/* Toggle panel button - fixed at bottom when panel is closed */}
          {!panelOpen && (
            <div className="absolute bottom-3 left-3 right-3 z-[400]">
              <Button
                data-testid="toggle-panel-btn"
                onClick={() => setPanelOpen(true)}
                className="w-full h-12 rounded-2xl shadow-lg text-sm font-semibold gap-2"
                style={{ background: '#2D6A4F', color: 'white' }}
              >
                <ChevronUp className="w-4 h-4" />
                {tab === "create" ? (editingParcel ? "Modifier la parcelle" : "Formulaire de saisie") : "Mes parcelles"}
                {points.length >= 3 && <Badge className="ml-1 text-[10px] px-1.5 border-0" style={{ background: 'rgba(255,255,255,0.25)', color: 'white' }}>{points.length} pts</Badge>}
              </Button>
            </div>
          )}
        </div>

        {/* Bottom sheet panel */}
        {panelOpen && (
          <div
            className="bg-white border-t border-black/5 flex flex-col shadow-[0_-8px_30px_rgba(0,0,0,0.12)]"
            style={{ height: '55vh', maxHeight: '55vh' }}
            data-testid="mobile-panel"
          >
            {/* Drag handle + collapse button */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-black/5 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-1 bg-black/10 rounded-full" />
                <span className="text-xs font-semibold" style={{ color: '#1A1A1A' }}>Parcelles manuelles</span>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPanelOpen(false)} data-testid="collapse-panel-btn">
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>
            {panelContent}
          </div>
        )}
      </div>
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div className="h-full flex" data-testid="manual-parcel-editor">
      {/* Left panel */}
      <div className="w-[380px] shrink-0 bg-white border-r border-black/5 flex flex-col h-full">
        <div className="p-4 border-b border-black/5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack} data-testid="back-to-map-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1A1A' }}>
                Parcelles manuelles
              </h2>
              <p className="text-[11px]" style={{ color: '#4A4A4A' }}>Saisie et gestion des parcelles GPS</p>
            </div>
          </div>
        </div>
        {panelContent}
      </div>

      {/* Right - Map */}
      <div className="flex-1 relative">
        <MapContainer center={mapCenter} zoom={mapZoom} maxZoom={MAX_ZOOM} doubleClickZoom={false} style={{ height: "100%", width: "100%" }} className="z-0 manual-editor-map">
          {mapContent}
        </MapContainer>

        {/* Top bar: instructions + GPS button */}
        <div className="absolute top-4 left-4 right-4 z-[400] flex items-center justify-between">
          <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-black/5">
            <p className="text-xs font-medium" style={{ color: '#1A1A1A' }}>
              <MapPin className="w-3 h-3 inline-block mr-1.5" style={{ color: '#2D6A4F' }} />
              Cliquez sur la carte pour placer des points GPS
            </p>
          </div>
          <Button
            data-testid="manual-geolocate-btn"
            variant="outline"
            size="icon"
            className="h-10 w-10 bg-white/95 backdrop-blur-sm shadow-sm border-black/5"
            onClick={handleGeolocate}
            disabled={locating}
            title="Ma position GPS"
          >
            <Navigation className={`w-4 h-4 ${locating ? 'animate-pulse' : ''}`} style={{ color: '#2D6A4F' }} />
          </Button>
        </div>
      </div>
    </div>
  );
}
